import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  makeStyles,
  TablePagination,
  TableSortLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { Search, Clear, FilterList } from "@material-ui/icons";
import { useLazyQuery } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { GET_CONSUMER_BOOKINGS_ADVANCED } from "../../../../graphql/bookings/consumerBookingsAdvancedQuery";
import { BookingStatus, IBooking } from "../../../../reducers/bookings/types";
import { StyledTableCell, StyledTableRow } from "../../../StyledTable";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { getLocalizedDateFormat, getLocalizedBookingSyntex } from "../../../../utils/localized.syntex";
import { DATE_TYPE } from "../../../utils";
import { BookingStatusText, isNoShowCancellation } from "../../utils";
import { VEHICLE_STATUS } from "../../Summary/const";
import { DateTime as d } from "luxon";
import { FLOW } from "../../../../reducers/website/types";
import { UserRole } from "../../../../reducers/auth/types";

const useStyles = makeStyles((theme) => ({
  chip: {
    fontSize: "12px",
    height: "24px",
    borderRadius: "12px",
    fontWeight: 500
  },
  filterCard: {
    padding: "16px",
    marginBottom: "16px",
    backgroundColor: "#f5f5f5"
  },
  tabs: {
    marginBottom: "16px",
    backgroundColor: "#fff",
    borderRadius: "4px"
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "11px"
  }
}));

type LocationState = {
  showActiveBookings?: boolean
};
interface IProps{
  flow?: FLOW;
}

const BookingsList: React.FC<IProps> = ({
  flow = FLOW.BOOKING
}) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { t } = useTranslation();
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { country } = website.organisation.address;
  const fleetAllocationFlow = flow === FLOW.FLEET_ALLOCATION
  const bookingFlow = flow === FLOW.BOOKING
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [searchableColumns, setSearchableColumns] = useState<string[]>([
    "referenceNumber",
    "status",
    "vehicle"
  ]);
  const [sortBy, setSortBy] = useState("pickupDateTime");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [tempStatuses, setTempStatuses] = useState<string[]>([]);
  const [tempPeriods, setTempPeriods] = useState<string[]>([]);

  useEffect(() => {
      const saved = localStorage.getItem("bookingFilters");
      if (!saved) return;

      try {
          const parsed = JSON.parse(saved);
          if (parsed.selectedStatuses) setSelectedStatuses(parsed.selectedStatuses);
          if (parsed.selectedPeriods) setSelectedPeriods(parsed.selectedPeriods);
          if (parsed.searchTerm) {
              setSearchInput(parsed.searchTerm);
              setSearchTerm(parsed.searchTerm);
          }
          if (parsed.sortBy) setSortBy(parsed.sortBy);
          if (parsed.sortOrder) setSortOrder(parsed.sortOrder);
          if (parsed.rowsPerPage) setRowsPerPage(parsed.rowsPerPage); 
      } catch (error) {
        throw error;
      }
  }, []);

  useEffect(() => {
      const saved = {
          selectedStatuses,
          selectedPeriods,
          searchTerm,
          sortBy,
          sortOrder,
          rowsPerPage,
      };
      localStorage.setItem("bookingFilters", JSON.stringify(saved));
  }, [
      selectedStatuses,
      selectedPeriods,
      searchTerm,
      sortBy,
      sortOrder,
      rowsPerPage,
  ]);

  useEffect(() => {
    if (filterModalOpen) {
      setTempStatuses(selectedStatuses);
      setTempPeriods(selectedPeriods);
    }
  }, [filterModalOpen, selectedStatuses, selectedPeriods]);


  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const [loadBookings, { loading, error }] = useLazyQuery(GET_CONSUMER_BOOKINGS_ADVANCED, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.consumerBookingsAdvanced) {
        let bookings = data.consumerBookingsAdvanced.bookings || [];
        if (location.state?.showActiveBookings) {
          bookings = bookings.filter((booking: IBooking) => {
            if (booking?.dropoffDateTime) {
              const dropoff = d.fromISO(booking?.dropoffDateTime)
              const now = d.now().setZone(booking.branch?.timeZone || "UTC");
              return dropoff > now;
            }
          })
        }
        setBookings(bookings);
        setTotalCount(data.consumerBookingsAdvanced.total || 0);
      }
    }
  });

  useEffect(() => {
    if (!user) return;

    const offset = page * rowsPerPage;
    
    const variables = {
      args: {
        customerId: user.businesscustomers?.id || user.customerId,
        searchTerm: searchTerm || null,
        searchColumns: searchableColumns,
        statuses: location.state?.showActiveBookings ? [BookingStatus.IN_PROGRESS] : selectedStatuses.length > 0 ? selectedStatuses : null,
        category: selectedPeriods.length > 0 ? selectedPeriods : ["all"],
        sortBy: sortBy,
        sortOrder: sortOrder,
        limit: rowsPerPage,
        offset: offset,
        isFleetAllocation: fleetAllocationFlow,
        ...(
          (website.organisation.enableBookingCreationForApprovedDriver && user?.role == UserRole.DRIVER)
          ? {
            approvedDriverId: user.id
          } : {}
        )
      }
    };

    loadBookings({ variables });
  }, [user, page, rowsPerPage, searchTerm, selectedStatuses, selectedPeriods, sortBy, sortOrder, searchableColumns, location.state, fleetAllocationFlow]);

  const getStatusColor = (status: BookingStatus): "default" | "primary" | "secondary" | "error" => {
    switch (status) {
      case BookingStatus.CONFIRMED:
      case BookingStatus.QUOTE:
        return "primary";
      case BookingStatus.IN_PROGRESS:
        return "secondary";
      case BookingStatus.CANCELLED:
      case BookingStatus.DECLINED:
        return "error";
      default:
        return "default";
    }
  };

  const getVehicleDisplayText = (bookingStatus: any, vehicle: any, vehicleStatus: string) => {
    if (
      [BookingStatus.IN_PROGRESS, BookingStatus.COMPLETED].includes(bookingStatus) &&
      vehicleStatus !== VEHICLE_STATUS.CONFIRMED
    ) {
      return vehicle.isGhostVehicle ? (
        <span style={{ color: "red" }}>{t("root:toBeAssigned")}</span>
      ) : (
        vehicle.licencePlate
      );
    }
    return "-";
  };

  const getVehiclesString = (booking: IBooking) => {
    const _vehicles = booking.currentBookingSchedules.map((bs) =>
      getVehicleDisplayText(booking.status, bs.vehicle, bs.vehicleStatus)
    );
    return (
      <>
        {_vehicles.slice(0, 1)}{" "}
        {_vehicles.length > 1 && (
          <span style={{ opacity: 0.4 }}>...{_vehicles.length - 1} More</span>
        )}
      </>
    );
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  const handleStatusFilterChange = useCallback((e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value as string[];
    setTempStatuses(value);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setSelectedStatuses(tempStatuses);
    setSelectedPeriods(tempPeriods);
    setPage(0);
    setFilterModalOpen(false);
  }, [tempStatuses, tempPeriods]);

  const handleResetFilters = useCallback(() => {
    setSelectedStatuses([]);
    setSelectedPeriods([]);
    setTempStatuses([]);
    setTempPeriods([]);
    setPage(0);
  }, []);

  const handlePeriodFilterChange = useCallback((event: any) => {
    const value = event.target.value as string[];
    setTempPeriods(value);
  }, []);

  const handleToggleSearchColumn = useCallback((column: string) => {
    setSearchableColumns((prev) =>
      prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column]
    );
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (column: string) => {
      const isAsc = sortBy === column && sortOrder === "asc";
      setSortOrder(isAsc ? "desc" : "asc");
      setSortBy(column);
      setPage(0);
    },
    [sortBy, sortOrder]
  );

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setSelectedStatuses([]);
    setSelectedPeriods([]);
    setTempPeriods([]);
    setTempStatuses([]);
    setPage(0);
    setSearchableColumns(["referenceNumber", "status", "vehicle"]);
    localStorage.removeItem("bookingFilters");
  }, []);

  const handleViewDetails = useCallback(
    (bookingId: string) => {
      history.push(`/account/${bookingFlow ? 'bookings': 'fleet-allocations'}/${bookingId}`);
    },
    [history]
  );

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading bookings: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: "24px" }}>
        <Typography variant="h3" style={{ fontWeight: 600, fontSize: "2rem" }}>
          {bookingFlow ? getLocalizedBookingSyntex(country) : t('root:fleetAllocations')}
        </Typography>

        <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            style={{
              height: "40px",
              textTransform: "none",
              borderColor: (selectedStatuses.length > 0 || selectedPeriods.length > 0) ? "#1976d2" : "rgba(0, 0, 0, 0.23)",
              color: (selectedStatuses.length > 0 || selectedPeriods.length > 0) ? "#1976d2" : "inherit"
            }}
          >
            Filters {(selectedStatuses.length + selectedPeriods.length) > 0 && `(${selectedStatuses.length + selectedPeriods.length})`}
          </Button>

          {(selectedStatuses.length > 0 || selectedPeriods.length > 0) && (
            <Button 
              variant="outlined" 
              startIcon={<Clear />} 
              onClick={handleClearFilters}
              style={{ 
                whiteSpace: "nowrap",
                height: "40px",
                textTransform: "none",
                borderColor: "rgba(0, 0, 0, 0.23)"
              }}
            >
              Clear All
            </Button>
          )}
        </Box>
      </Box>

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 600 }}>FILTERS</Typography>
            <Box>
              <Button
                color="primary"
                onClick={handleResetFilters}
                style={{ textTransform: "none", marginRight: 8 }}
              >
                RESET
              </Button>
              <IconButton size="small" onClick={() => setFilterModalOpen(false)}>
                <Clear />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Booking Period</InputLabel>
                <Select
                  multiple
                  value={tempPeriods}
                  onChange={handlePeriodFilterChange}
                  MenuProps={{
                    getContentAnchorEl: null
                  }}
                  input={<OutlinedInput label="Booking Period" />}
                  renderValue={(selected) => 
                    (selected as string[]).length === 0 ? "All" : (selected as string[])
                      .map((s) => {
                        if (s === "current") return t("root:current");
                        if (s === "upcoming") return t("root:upcoming");
                        if (s === "past") return t("root:past");
                        return s;
                      })
                      .join(", ")
                  }
                >
                  <MenuItem value="current">
                    <Checkbox checked={tempPeriods.includes("current")} />
                    <ListItemText primary={t("root:current")} />
                  </MenuItem>
                  <MenuItem value="upcoming">
                    <Checkbox checked={tempPeriods.includes("upcoming")} />
                    <ListItemText primary={t("root:upcoming")} />
                  </MenuItem>
                  <MenuItem value="past">
                    <Checkbox checked={tempPeriods.includes("past")} />
                    <ListItemText primary={t("root:past")} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={tempStatuses}
                  onChange={handleStatusFilterChange}
                  MenuProps={{
                    getContentAnchorEl: null
                  }}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => 
                    (selected as string[]).length === 0 ? "All" : (selected as string[])
                    //FIXME : TS error due to Dynamic i18n key
                      .map((s) => t(`root:currentBookingStatus.${BookingStatusText[s]}`))
                      .join(", ")
                  }
                >
                  <MenuItem value={BookingStatus.CONFIRMED}>
                    <Checkbox checked={tempStatuses.includes(BookingStatus.CONFIRMED)} />
                    <ListItemText primary="Confirmed" />
                  </MenuItem>
                  {!fleetAllocationFlow && (
                    <MenuItem value={BookingStatus.QUOTE}>
                      <Checkbox checked={tempStatuses.includes(BookingStatus.QUOTE)} />
                      <ListItemText primary="Quote" />
                    </MenuItem>
                  )}
                  <MenuItem value={BookingStatus.IN_PROGRESS}>
                    <Checkbox checked={tempStatuses.includes(BookingStatus.IN_PROGRESS)} />
                    <ListItemText primary="In Progress" />
                  </MenuItem>
                  <MenuItem value={BookingStatus.COMPLETED}>
                    <Checkbox checked={tempStatuses.includes(BookingStatus.COMPLETED)} />
                    <ListItemText primary="Completed" />
                  </MenuItem>
                  <MenuItem value={BookingStatus.CANCELLED}>
                    <Checkbox checked={tempStatuses.includes(BookingStatus.CANCELLED)} />
                    <ListItemText primary="Cancelled" />
                  </MenuItem>
                  <MenuItem value={BookingStatus.DECLINED}>
                    <Checkbox checked={tempStatuses.includes(BookingStatus.DECLINED)} />
                    <ListItemText primary="Declined" />
                  </MenuItem>
                  {!fleetAllocationFlow && (
                    <MenuItem value={BookingStatus.REQUIRES_APPROVAL}>
                      <Checkbox checked={tempStatuses.includes(BookingStatus.REQUIRES_APPROVAL)} />
                      <ListItemText primary="Requires Approval" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
          onClick={handleApplyFilters} color="primary" variant="contained"
          disabled={tempStatuses.length === 0 && tempPeriods.length === 0}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Card className={classes.filterCard}>
        <Grid container spacing={2} alignItems="flex-start">
          {(selectedStatuses.length > 0 || selectedPeriods.length > 0) && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: "8px" }}>
                <Typography variant="body2" style={{ fontWeight: 600, marginRight: 8 }}>
                  Active Filters:
                </Typography>
                {selectedPeriods.map((period) => (
                  <Chip
                    key={period}
                    label={`Period: ${
                      period === "current" ? t("root:current") : 
                      period === "upcoming" ? t("root:upcoming") : 
                      t("root:past")
                    }`}
                    onDelete={() => {
                      setSelectedPeriods(selectedPeriods.filter(p => p !== period));
                      setPage(0);
                    }}
                    color="primary"
                    size="small"
                    className={classes.chip}
                  />
                ))}
                {selectedStatuses.map((status) => (
                  <Chip
                    key={status}
                    label={`Status: ${t(`root:currentBookingStatus.${BookingStatusText[status as BookingStatus]}` as any)}`}

                    onDelete={() => {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                      setPage(0);
                    }}
                    color="primary"
                    size="small"
                    className={classes.chip}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search across all columns... (e.g., 'CONFIRMED CAM123')"
              value={searchInput}
              onChange={handleSearchChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput("");
                        setSearchTerm("");
                      }}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Card>

      <Box style={{ position: "relative" }}>
        {loading && (
          <Box
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1000
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <TableContainer style={{ maxHeight: "calc(100vh - 450px)", minHeight: "400px" }} className="row-hover">
          <Table stickyHeader aria-label="bookings table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "referenceNumber"}
                    direction={sortBy === "referenceNumber" ? sortOrder : "asc"}
                    onClick={() => handleSort("referenceNumber")}
                  >
                    {`${getLocalizedBookingSyntex(country)} ${t("root:ref")}`}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "status"}
                    direction={sortBy === "status" ? sortOrder : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    {t("root:status")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "pickupDateTime"}
                    direction={sortBy === "pickupDateTime" ? sortOrder : "asc"}
                    onClick={() => handleSort("pickupDateTime")}
                  >
                    {t("root:startDate")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "dropoffDateTime"}
                    direction={sortBy === "dropoffDateTime" ? sortOrder : "asc"}
                    onClick={() => handleSort("dropoffDateTime")}
                  >
                    {t("root:endDate")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>{t("root:vehicles")}</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {bookings.length ? (
                bookings.map((booking) => (
                  <StyledTableRow
                    key={booking.id}
                    onClick={() => handleViewDetails(booking.id)}
                    style={{ cursor: "pointer" }}
                    hover
                  >
                    <StyledTableCell>
                      <span style={{ color: "rgb(24, 117, 210)", fontWeight: 600 }}>
                        {booking.referenceNumber}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Tooltip
                        title={
                          isNoShowCancellation(booking.cancellationReason)
                            ? "Cancelled - Grace Period Exceeded"
                            : ""
                        }
                        disableHoverListener={!isNoShowCancellation(booking.cancellationReason)}
                      >
                        <Chip
                          label={t(`root:currentBookingStatus.${BookingStatusText[booking.status]}`)}
                          //FIXME
                          color={getStatusColor(booking.status)}
                          size="small"
                          className={classes.chip}
                        />
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell>
                      {getLocalizedDateFormat(
                        country,
                        booking.pickupDateTime,
                        DATE_TYPE.EXPANDED,
                        booking.branch?.timeZone
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {booking.dropoffDateTime
                        ? getLocalizedDateFormat(
                            country,
                            booking.dropoffDateTime,
                            DATE_TYPE.EXPANDED,
                            booking.branch?.timeZone
                          )
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell>{getVehiclesString(booking)}</StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5}>
                    <Typography style={{ textAlign: "center", opacity: 0.5, padding: "40px 0" }}>
                      {t("root:noRecordFound")}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box style={{ borderTop: "1px solid #e0e0e0", paddingTop: "8px" }}>
          <TablePagination
            key={`pagination-${page}-${rowsPerPage}-${totalCount}`}
            rowsPerPageOptions={[10, 20, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            SelectProps={{
              inputProps: { "aria-label": "rows per page" },
              native: true
            }}
          />
        </Box>
      </Box>
    </div>
  );
};

export default BookingsList;

